/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call -- Linter errors in this file to be resolved in Story 2828164 */
import { FXRates, ProposablePortfolio_TEMP } from '@blk/aw-api-clients';
import {
  ColumnDefV2Format,
  ColumnSource,
  ColumnType,
  ConfigurableHoldingColumn,
  HoldingsInitialSort,
  LeverageConfig,
  SecurityCustomColumnDef,
  SleeveInfo,
} from '@blk/aw-bff-typescript-models';
import { ProposalAwaAnalysis, Security, TotalPortfolioAnalysis } from '@blk/awt-aladdinportfoliobuilderserver';
import { Entity } from '@blk/awt-aladdinwealthserver';
import {
  ColDef,
  ColGroupDef,
  Column,
  GridApi,
  HeaderLocation,
  HeaderValueGetterParams,
  IAggFuncParams,
  ICellEditorParams,
  ICellRendererParams,
  IHeaderParams,
  IRowNode,
  ProcessCellForExportParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { TFunction } from 'i18next';
import { compact, get, isEmpty, isNaN, isNil, mean, sum } from 'lodash';
import { getSecurityAnalyticsSelectorByName } from './AnalyticsColumnUtils';
import { getEntityGivenAggIDTag } from './EntityDefsUtils';
import { getCurrencyConvertedValue } from './FxUtils';
import { search } from './JmespathUtils';
import type { CurrencyFormatter, FlexibleValueFormatter, ValueFormatter } from './NumberUtils';
import {
  columnDefValueFormatters,
  isCurrencyFormatter,
  isExpressionBasedCurrencyFormatter,
  isValueFormatter,
  isValueFormatterName,
  valueFormatters,
} from './NumberUtils';
import { ONE_DASH } from './TextUtils';

export interface SleeveInfoStoreState {
  [key: string]: SleeveInfo | undefined;
}

export type GridRowSelectionType = 'single' | 'multiple' | undefined;

export type DataGridValueFormatterParams = Pick<ValueFormatterParams, 'data' | 'value' | 'context'>;

const SecurityDescriptionColId = 'security.description';
export const SecurityDescriptionMinWidth = 250;

/**
 *
 * Linter errors in this file to be resolved in Story 2828164
 *
 */

// currency formats will try to source currency type from ag-grid context and fallback to sourcing from row data if no context provided
export const columnDefDataGridValueFormatters: {
  [P in ColumnDefV2Format]: (gridColumn: DataGridValueFormatterParams) => string;
} = {
  NUMERIC_WHOLE_NUMBER: ({ value }: DataGridValueFormatterParams) => valueFormatters.numericWholeNumber(value),
  NUMERIC: ({ value }: DataGridValueFormatterParams) => valueFormatters.numeric(value),
  NUMERIC_INVALID_ZERO: ({ value }: DataGridValueFormatterParams) => valueFormatters.numericInvalidZero(value),
  NUMERIC_SHORTHAND: ({ value }: DataGridValueFormatterParams) => valueFormatters.numericShorthand(value),
  NUMERIC_FOUR_DECIMALS: ({ value }: DataGridValueFormatterParams) => valueFormatters.numericFourDecimals(value),
  NUMERIC_TWO_DECIMALS: ({ value }: DataGridValueFormatterParams) => valueFormatters.numericTwoDecimals(value),
  CURRENCY: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currency(context?.currency ?? data?.currency)(value),
  CURRENCY_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currencyWithSymbol(context?.currency ?? data?.currency)(value),
  CURRENCY_WITH_SYMBOL_POSTFIX: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currencyWithSymbolPostfix(context?.currency ?? data?.currency)(value),
  CURRENCY_AUTO: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currencyAuto(context?.currency ?? data?.currency)(value),
  CURRENCY_SHORTHAND_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currencyShorthandWithSymbol(context?.currency ?? data?.currency)(value),
  CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.currencyWholeNumberWithSymbol(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrency(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyWithSymbol(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_WITH_SYMBOL_POSTFIX: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyWithSymbolPostfix(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_AUTO: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyAuto(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_SHORTHAND: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyShorthand(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_SHORTHAND_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyShorthandWithSymbol(context?.currency ?? data?.currency)(value),
  MULTI_CURRENCY_WHOLE_NUMBER_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.multiCurrencyWholeNumberWithSymbol(context?.currency ?? data?.currency)(value),
  EXPRESSION_BASED_CURRENCY_WITH_SYMBOL: ({ value, context, data }: DataGridValueFormatterParams) =>
    valueFormatters.expressionBasedCurrencyWithSymbol(
      context?.currency ?? data?.currency,
      context?.currencyExpression,
      data
    )(value),
  PERCENTAGE: ({ value }: DataGridValueFormatterParams) => valueFormatters.percentage(value),
  PERCENTAGE_WITHOUT_SYMBOL: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.percentageWithoutSymbol(value),
  DECIMAL_AS_PERCENTAGE: ({ value }: DataGridValueFormatterParams) => valueFormatters.decimalAsPercentage(value),
  DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.decimalAsPercentageWithoutSymbol(value),
  ONE_DECIMAL_AS_PERCENTAGE: ({ value }: DataGridValueFormatterParams) => valueFormatters.oneDecimalAsPercentage(value),
  ONE_DECIMAL_AS_PERCENTAGE_WITHOUT_SYMBOL: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.oneDecimalAsPercentageWithoutSymbol(value),
  FOUR_DECIMALS_AS_PERCENTAGE: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.fourDecimalsAsPercentage(value),
  FOUR_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.fourDecimalsAsPercentageWithoutSymbol(value),
  ZERO_DECIMALS_AS_PERCENTAGE: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.zeroDecimalsAsPercentage(value),
  ZERO_DECIMALS_AS_PERCENTAGE_WITHOUT_SYMBOL: ({ value }: DataGridValueFormatterParams) =>
    valueFormatters.zeroDecimalsAsPercentageWithoutSymbol(value),
  DATETIME_AS_ISO_STRING: ({ value }: DataGridValueFormatterParams) => valueFormatters.datetime(value),
  TIME_AS_ISO_STRING: ({ value }: DataGridValueFormatterParams) => valueFormatters.time(value),
  DATE_AS_ISO_STRING: ({ value }: DataGridValueFormatterParams) => valueFormatters.date(value),
  YEAR_MONTH_DAY: ({ value }: DataGridValueFormatterParams) => valueFormatters.yearMonthDay(value),
  MONTH_DAY_YEAR: ({ value }: DataGridValueFormatterParams) => valueFormatters.monthDayYear(value),
  STRING_UPPERCASE: ({ value }: DataGridValueFormatterParams) => valueFormatters.stringUppercase(value)(0),
  STRING_LOWERCASE: ({ value }: DataGridValueFormatterParams) => valueFormatters.stringLowercase(value)(0),
  STRING_START_CASE: ({ value }: DataGridValueFormatterParams) => valueFormatters.stringStartCase(value)(0),
  STRING_UPPER_FIRST: ({ value }: DataGridValueFormatterParams) => valueFormatters.stringUpperFirst(value)(0),
  INTRADAY: ({ value, data }: DataGridValueFormatterParams) =>
    data?.isIntradayNew || data?.isIntradayUpdated ? '-' : valueFormatters.defaultFormatter(value),
};

export const formatCurrencyWithColumnData = (params: ValueFormatterParams) => {
  const { value, data } = params;
  if (data) {
    return valueFormatters.currency(data.currency)(value);
  }
  return '';
};

interface CreateCustomGroupByColumnsParams {
  existingColumns: ColDef[];
  t: TFunction;
  userConfiguredGroupByColumns: ConfigurableHoldingColumn[];
}

export function createCustomGroupByColumns({
  t,
  existingColumns,
  userConfiguredGroupByColumns,
}: CreateCustomGroupByColumnsParams) {
  let customGroupByColumns: ColDef[] = [];
  userConfiguredGroupByColumns.forEach(({ name, description, field, fallbackFields }) => {
    const existingColumn = existingColumns.find(({ colId }) => colId === name);
    if (field) {
      if (existingColumn && field === existingColumn.field) {
        throw new Error(`Cannot overwrite existing column definition : ${name}`);
      } else {
        customGroupByColumns.push({
          headerName: t(description),
          colId: name,
          field,
          cellRenderer: 'agGroupCellRenderer',
          hide: true,
          headerClass: 'groupByOnly',
          width: 1,
          suppressSizeToFit: true,
          valueGetter: fallbackValueGetterFactory({ fallbackFields, t }),
        });
      }
    }
  });
  return customGroupByColumns;
}

type FallbackValueGetterFactoryOptions = { fallbackFields?: string[]; t: TFunction };

export function fallbackValueGetterFactory(
  options: FallbackValueGetterFactoryOptions
): (params: ValueGetterParams) => unknown {
  const { fallbackFields, t } = options;
  return ({ colDef, data }) => {
    if (data && colDef.field) {
      let fieldsToTest = [colDef.field];
      if (fallbackFields?.length) {
        fieldsToTest = fieldsToTest.concat(fallbackFields);
      }
      let valueToReturn;
      for (const fieldToTest of fieldsToTest) {
        const formattedFieldToTest = fieldToTest
          .split('.')
          .map((part) => (/^\d/.test(part) ? `"${part}"` : part))
          .join('.');

        if (!fieldToTest) {
          continue;
        }
        try {
          valueToReturn = search(data, formattedFieldToTest);
        } catch (e) {
          console.error(`Error displaying the field at ${fieldToTest}`, e);
        }
        if (!isNil(valueToReturn)) {
          return valueToReturn as unknown;
        }
        if (/^security\./.exec(fieldToTest) && !data.security) {
          return null;
        }
        if (/^posInfo\./.exec(fieldToTest) && !data.posInfo) {
          return null;
        }
      }
      return !isNil(valueToReturn) ? valueToReturn : t('c_unknown');
    }
    return null;
  };
}

const isNumericColumn = (type: ColumnType | undefined) =>
  type !== 'text' && type !== 'textWithInput' && type !== 'editableAccountId';

/**
 * Shared helper to determine the aggFunc for a given column across different builders.
 * - TAX_ANALYTICS columns always use customSum.
 * - CONTRIBUTION columns should use the selector-provided aggFunc when available.
 * - Otherwise fall back to the column config's custom agg func.
 */
function getAggFunc(
  column: ConfigurableHoldingColumn,
  columnSource?: ColumnSource,
  currentSecurityAnalyticsSelector?: { aggFunc?: string }
): string | undefined {
  if (columnSource === 'TAX_ANALYTICS') {
    return 'customSum';
  }
  if (columnSource === 'CONTRIBUTION') {
    return currentSecurityAnalyticsSelector?.aggFunc ?? getCustomAggFuncForColumn(column);
  }
  return getCustomAggFuncForColumn(column);
}

/**
 * Utility function to determine the custom aggregation method based on column configuration.
 * Sum, Average, and Weighted average methods can only be applied to numeric columns.
 * If a custom aggregation method is not set/enabled, returns undefined.
 *
 * @param column - ConfigurableHoldingColumn with enableCustomGroupedCalc and aggregationMethod properties
 * @returns The name of the custom aggregation function ('customSum', 'customAvg', etc.) or undefined
 */
function getCustomAggFuncForColumn(column: ConfigurableHoldingColumn): string | undefined {
  if (column.enableCustomGroupedCalc && column.aggregationMethod) {
    switch (column.aggregationMethod) {
      case 'SUM': {
        return isNumericColumn(column.type) ? 'customSum' : undefined;
      }
      case 'AVERAGE': {
        return isNumericColumn(column.type) ? 'customAvg' : undefined;
      }
      case 'WEIGHTED_AVERAGE': {
        return isNumericColumn(column.type) ? 'customWeightedAvg' : undefined;
      }
      case 'FIRST': {
        return 'customFirst';
      }
    }
  }
  return undefined;
}

export function createGridColDefsFromConfig(
  baseColumns: ConfigurableHoldingColumn[],
  options: Partial<ColDef>,
  t: TFunction,
  cellRenderer?: (params: ICellRendererParams) => React.JSX.Element,
  currency?: string,
  isAggregateView?: boolean,
  fieldToValueGetterMap?: { [key: string]: (params: ValueGetterParams) => string | null | undefined }
): ColDef[] {
  return baseColumns.map<ColDef>((colDef) => {
    const customValueGetter = colDef.field && fieldToValueGetterMap ? fieldToValueGetterMap[colDef.field] : undefined;
    return {
      initialWidth: colDef.width ?? 100,
      // setting a minimum width to avoid the description column being too small when columns groups are opened/collapsed(during groupings)
      minWidth: colDef.field === SecurityDescriptionColId ? SecurityDescriptionMinWidth : undefined,
      flex: colDef.field === SecurityDescriptionColId ? 2 : undefined,
      colId: colDef.name,
      headerName: t(colDef.description),
      field: colDef.field,
      valueGetter: customValueGetter ?? fallbackValueGetterFactory({ fallbackFields: colDef.fallbackFields, t }),
      suppressSizeToFit: false,
      type: colDef.type,
      cellRenderer: colDef.type ? undefined : cellRenderer,
      pinned: colDef.pinned && !isAggregateView ? 'left' : undefined,
      valueFormatter: currency && getAgGridValueFormatterForConfigurableHoldingColumn(colDef, currency),
      comparator: isNumericColumn(colDef.type) ? numberStringComparator : undefined,
      aggFunc: getAggFunc(colDef, colDef.source),
      ...options,
    };
  });
}
export function createGridColDefsFromConfigForSecurityCustomColumnDef(
  baseColumns: SecurityCustomColumnDef[],
  options: Partial<ColDef>,
  t: TFunction
): ColDef[] {
  return baseColumns.map((colDef) => ({
    headerName: t(colDef.description),
    field: colDef.field,
    type: colDef.type,
    name: colDef.name,
    ...options,
  }));
}

export function getAgGridValueFormatterForColumnDefV2(
  format: ColumnDefV2Format,
  currency?: string,
  /*
   * overwrites null value formatter when applicable
   * currently datetime utils explicitly return "Invalid Date" when date is nil
   */
  nullValueFormatter?: (value: null | undefined) => string,
  language?: string
): undefined | ((params: DataGridValueFormatterParams) => string) {
  if (format && columnDefValueFormatters.hasOwnProperty(format)) {
    if (format === 'DATETIME_AS_ISO_STRING' || format === 'DATE_AS_ISO_STRING' || format === 'TIME_AS_ISO_STRING') {
      // Invariant: date/time formatters accept (value, language?) and grid values are number|string for date fields.
      const formatterMap: Record<
        'DATETIME_AS_ISO_STRING' | 'DATE_AS_ISO_STRING' | 'TIME_AS_ISO_STRING',
        FlexibleValueFormatter
      > = {
        DATETIME_AS_ISO_STRING: valueFormatters.datetime,
        DATE_AS_ISO_STRING: valueFormatters.date,
        TIME_AS_ISO_STRING: valueFormatters.time,
      };
      const dateFormatter = formatterMap[format];
      return ({ value }) => {
        if (isNil(value) && typeof nullValueFormatter === 'function') {
          return nullValueFormatter(value);
        }
        const valueToFormat = typeof value === 'string' || typeof value === 'number' ? value : String(value);
        return dateFormatter(valueToFormat, language);
      };
    }
    const formatter = columnDefValueFormatters[format];
    if (isCurrencyFormatter(formatter) && currency) {
      return ({ value }) =>
        isNil(value) && typeof nullValueFormatter === 'function'
          ? nullValueFormatter(value)
          : formatter(currency)(value);
    }
    return ({ value }) =>
      isNil(value) && typeof nullValueFormatter === 'function'
        ? nullValueFormatter(value)
        : (formatter as ValueFormatter)(value);
  }
  return undefined;
}

export function getAgGridValueFormatterForConfigurableHoldingColumn(
  column: ConfigurableHoldingColumn,
  currency: string,
  contributorsLimit?: number
): undefined | ((params: DataGridValueFormatterParams) => string) {
  if (column.format) {
    const formatter = columnDefValueFormatters[column.format];
    // Special handling for expression-based formatters that need row data context
    if (isExpressionBasedCurrencyFormatter(formatter) && column.currencyExpression) {
      return ({ value, data }: DataGridValueFormatterParams) =>
        valueFormatters.expressionBasedCurrencyWithSymbol(currency, column.currencyExpression, data)(value);
    }
    // Use the standard ColumnDefV2Format formatter
    return getAgGridValueFormatterForColumnDefV2(column.format, currency);
  }

  if (column.type) {
    if (isValueFormatterName(column.type)) {
      const valueFormatter = valueFormatters[column.type];
      if (isCurrencyFormatter(valueFormatter)) {
        return ({ value }) => valueFormatter(currency)(value);
      }
      if (column.type === 'numeric' && (contributorsLimit || column.sleeve)) {
        return ({ value }) => columnDefValueFormatters.NUMERIC_INVALID_ZERO(value);
      }
      return ({ value }) => valueFormatter(value);
    }
  }
  return undefined;
}

interface CreateAnalyticsGridColDefsFromConfigParams {
  additionalColumns: ConfigurableHoldingColumn[];
  analysisParams?: {
    getTotalPortfolioAnalysis?: (key: keyof ProposalAwaAnalysis) => TotalPortfolioAnalysis | undefined;
    leverage?: LeverageConfig;
    currentTopLevelGrouping?: string;
  };
  contributorsLimit?: number;
  currency: string;
  enableSplitAnalyticsColumnGroups?: boolean;
  entityDefs?: Entity[];
  fxRates?: FXRates;
  getCloakValue?: (aggIDTag: string, aggIDValue: string) => string;
  hide?: boolean;
  options: Partial<ColDef>;
  portfoliosCurrencyMap?: Record<string, string>;
  selectedAdditionalColumnNames: string[];
  t: TFunction;
}

interface CreateAnalyticsGridColDefsFromConfigReturn {
  currentAnalyticsColumns: ColDef[];
  differenceAnalyticsColumn: ColDef[];
  posInfoColumns: ColDef[];
  proposedAnalyticsColumns: ColDef[];
  securityColumns: ColDef[];
  taxAnalyticsColumns: ColDef[];
}

export const TOTAL_ROW_ID = '___TOTAL___';
export const nodeIsTotalRow = (node?: IRowNode | null) => node?.data?.id === TOTAL_ROW_ID;

/**
 * Formats cell values for AG Grid columns that may contain sensitive data requiring cloaking.
 *
 * Skips cloaking when fallbackValueGetterFactory returns t('c_unknown') for missing posInfo fields
 * (e.g., cash accounts without GRUPO_ID). Calling getCloakValue with this placeholder would result in unresolved promises.
 *
 * @param value - The raw cell value to format
 * @param colId - The column ID to look up entity definition
 * @param getCloakValue - Function to retrieve cloaked value for sensitive data
 * @param entityDefs - Array of entity definitions to match against colId
 * @param t - Translation function to check for 'c_unknown' placeholder
 * @returns The formatted value (cloaked if sensitive, original otherwise)
 */
export const gridCloakValueFormatter = (
  value: string,
  colId: string,
  getCloakValue: (aggIDTag: string, aggIDValue: string) => string,
  entityDefs: Entity[],
  t: TFunction
): string => {
  if (value === t('c_unknown')) {
    return String(value);
  }

  const foundEntityDef = getEntityGivenAggIDTag(entityDefs, colId);
  if (foundEntityDef) {
    const cloakedValue = getCloakValue(foundEntityDef.aggIDTag, value);
    return String(cloakedValue);
  }
  return String(value);
};

interface ColumnsBuilderParams {
  columnDefs: ConfigurableHoldingColumn[];
  columnSource: ColumnSource;
  contributorsLimit?: number;
  entityDefs?: Entity[];
  fieldGetter: (columnName: string, sleeve?: string) => string;
  getCloakValue?: (aggIDTag: string, aggIDValue: string) => string;
  totalPortfolioAnalysis?: TotalPortfolioAnalysis;
}

export function numberStringComparator(valueA: string | number, valueB: string | number) {
  const numA = typeof valueA === 'string' ? parseFloat(valueA) : valueA;
  const numB = typeof valueB === 'string' ? parseFloat(valueB) : valueB;

  if (isNaN(numA) && isNaN(numB)) {
    return valueA.toString().localeCompare(valueB.toString());
  } else if (isNaN(numA)) {
    return -1;
  } else if (isNaN(numB)) {
    return 1;
  }
  return numA - numB;
}

// Define a type guard for position data
interface PositionData {
  portfolioId: string;
}

export function hasPortfolioId(data: unknown): data is PositionData {
  return (
    data !== null &&
    typeof data === 'object' &&
    'portfolioId' in data &&
    data.portfolioId !== null &&
    typeof data.portfolioId === 'string'
  );
}

/**
 * Delimiter used to identify split analytics columns and separate root column tag from column group label.
 */
export const PIPE_DELIMITER = '|';

/**
 * Utility function to check if a column name is a split analytics column.
 * This is used when split column groups are enabled.
 * @param columnName
 */
export const isColumnInSplitGroup = (columnName: string) => columnName.includes(PIPE_DELIMITER);

/**
 * Utility function to split a split analytics column tag into its root column tag and current column group label.
 * This is used when split column groups are enabled to identify the correct column definition and its assigned column group.
 * @param colTag
 */
export const splitColTagIntoSplitGroupParts = (colTag: string): { columnName: string; columnGroupLabel?: string } => {
  if (isColumnInSplitGroup(colTag)) {
    const [columnName, columnGroupLabel] = colTag.split(PIPE_DELIMITER);
    return { columnName, columnGroupLabel };
  }
  return { columnName: colTag };
};

/**
 * Utility function to filter column definitions by their column source.
 * Analytic columns are additionally filtered by their column group label when split column groups are enabled.
 * Defaults to name comparison when split column groups are not enabled or colName does not include a pipe delimiter.
 * @param selectedAdditionalColumnNames
 * @param additionalColumns
 * @param columnGroupLabel
 * @param enableSplitAnalyticsColumnGroups
 */
interface GetColumnsByColumnSourceParams {
  additionalColumns: ConfigurableHoldingColumn[];
  columnGroupName?: string;
  columnSource: ColumnSource;
  enableSplitAnalyticsColumnGroups: boolean;
  selectedAdditionalColumnNames: string[];
}
const getColDefsByColumnSource = ({
  selectedAdditionalColumnNames,
  additionalColumns,
  enableSplitAnalyticsColumnGroups,
  columnSource,
  columnGroupName,
}: GetColumnsByColumnSourceParams) =>
  compact(
    selectedAdditionalColumnNames.map((colName) =>
      additionalColumns
        .filter((c) => c.source === columnSource)
        .find((col) => {
          if (enableSplitAnalyticsColumnGroups && isColumnInSplitGroup(colName)) {
            const { columnName, columnGroupLabel } = splitColTagIntoSplitGroupParts(colName);
            // only need to compare group names for analytics columns
            return columnGroupLabel && col.source === 'CONTRIBUTION'
              ? col.name === columnName && columnGroupLabel === columnGroupName
              : col.name === columnName;
          }
          return col.name === colName;
        })
    )
  );

export function createAnalyticsGridColDefsFromConfig({
  selectedAdditionalColumnNames,
  additionalColumns,
  currency,
  portfoliosCurrencyMap,
  options,
  t,
  contributorsLimit,
  analysisParams,
  getCloakValue,
  entityDefs,
  hide = false,
  fxRates,
  enableSplitAnalyticsColumnGroups = false,
}: CreateAnalyticsGridColDefsFromConfigParams): CreateAnalyticsGridColDefsFromConfigReturn {
  const { getTotalPortfolioAnalysis, leverage, currentTopLevelGrouping } = analysisParams || {};
  const columnsBuilder = ({
    columnDefs,
    fieldGetter,
    entityDefs,
    columnSource,
    contributorsLimit,
    totalPortfolioAnalysis,
    getCloakValue,
  }: ColumnsBuilderParams): ColDef[] =>
    columnDefs.map((column) => {
      const currentSecurityAnalyticsSelector = getSecurityAnalyticsSelectorByName(column.name);
      const factoryParams = {
        leverage,
        totalPortfolioAnalysis,
        currentTopLevelGrouping,
        column,
      };
      const getValueFormatter = () => {
        if (columnSource === 'TAX_ANALYTICS') {
          return columnDefDataGridValueFormatters.CURRENCY_WITH_SYMBOL;
        } else if (columnSource === 'POSINFO' && getCloakValue && entityDefs) {
          return (params: ValueFormatterParams<unknown, string | number>) => {
            const { value, data, node } = params;
            if (column.type === 'text' && nodeIsTotalRow(node)) {
              return ONE_DASH;
            }
            if (value === null || value === undefined) {
              return ONE_DASH;
            }

            const cloakedValue = gridCloakValueFormatter(String(value), column.name, getCloakValue, entityDefs, t);
            const wasCloakedValueFound = String(value) !== cloakedValue;
            const type = column.type;

            if (wasCloakedValueFound) {
              // no need to format further if the value was cloaked
              return cloakedValue;
            } else if (type && isValueFormatterName(type)) {
              if (column.format === 'EXPRESSION_BASED_CURRENCY_WITH_SYMBOL' && column.currencyExpression) {
                // Use the expression-based formatter from columnDefDataGridValueFormatters
                return columnDefDataGridValueFormatters.EXPRESSION_BASED_CURRENCY_WITH_SYMBOL({
                  value: Number(value),
                  context: { currencyExpression: column.currencyExpression, currency },
                  data,
                });
              }

              const valueFormatter: ValueFormatter | CurrencyFormatter = valueFormatters[type];

              if (isCurrencyFormatter(valueFormatter) && currency) {
                let convertedValue = value;
                /**
                 * Currency conversion for posInfo fields at the aggregate level:
                 * The posInfo contains values in the position's portfolio currency. When aggregating
                 * across portfolios with different base currencies, we convert these values to the
                 * aggregate currency for accurate cross-portfolio comparison and summation.
                 * portfoliosCurrencyMap is only available when we are at the aggregate level.
                 */
                if (
                  data &&
                  hasPortfolioId(data) &&
                  data.portfolioId &&
                  portfoliosCurrencyMap &&
                  portfoliosCurrencyMap[data.portfolioId] !== currency &&
                  fxRates
                ) {
                  convertedValue = getCurrencyConvertedValue(
                    Number(value),
                    portfoliosCurrencyMap[data.portfolioId],
                    currency,
                    2,
                    fxRates
                  );
                }
                return valueFormatter(currency)(Number(convertedValue));
              }
              return isValueFormatter(valueFormatter) ? valueFormatter(Number(value)) : String(value);
            }
            return String(value);
          };
        }
        return getAgGridValueFormatterForConfigurableHoldingColumn(column, currency, contributorsLimit);
      };

      return {
        ...options,
        hide,
        initialWidth: column.width ?? 100,
        minWidth: column.type !== 'text' ? 84 : 64,
        colId: column.name,
        headerName: t(column.description),
        // "column.name" is the additional column being added using settings in the holdings table
        field: column.field ?? fieldGetter(column.name, column.sleeve),
        valueGetter: currentSecurityAnalyticsSelector.valueGetterFactory
          ? currentSecurityAnalyticsSelector.valueGetterFactory(factoryParams)
          : fallbackValueGetterFactory({ fallbackFields: column.fallbackFields, t }),
        suppressSizeToFit: true,
        type: column.type !== 'text' ? 'auxNumberColumn' : undefined,
        cellDataType: column.type === 'text' ? 'text' : undefined,
        valueFormatter: getValueFormatter(),
        comparator: column.type === 'numeric' ? numberStringComparator : undefined,
        aggFunc: getAggFunc(column, columnSource, currentSecurityAnalyticsSelector),
      };
    });

  const posInfoColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'POSINFO',
  });
  const securityColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'SECURITY',
  });
  const currentAnalyticsColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'CONTRIBUTION',
    columnGroupName: t('c_current'),
  });
  const proposedAnalyticsColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'CONTRIBUTION',
    columnGroupName: t('c_proposed'),
  });
  const differenceAnalyticsColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'CONTRIBUTION',
    columnGroupName: t('c_difference'),
  });
  const taxAnalyticsColumnDefs = getColDefsByColumnSource({
    selectedAdditionalColumnNames,
    additionalColumns,
    enableSplitAnalyticsColumnGroups,
    columnSource: 'TAX_ANALYTICS',
  });

  const getFieldGetter =
    (originalOrProposedOrDifference: 'original' | 'proposed' | 'difference') =>
    (columnName: string, columnSleeve: string | undefined): string => {
      const prefix = columnSleeve ? `sleevedAnalytics.${columnSleeve}` : 'analytics';
      return `${prefix}.${columnName}.${originalOrProposedOrDifference}`;
    };

  const currentAnalyticsColumns = columnsBuilder({
    columnDefs: currentAnalyticsColumnDefs,
    fieldGetter: getFieldGetter('original'),
    columnSource: 'CONTRIBUTION',
    contributorsLimit,
    totalPortfolioAnalysis: getTotalPortfolioAnalysis ? getTotalPortfolioAnalysis('original') : undefined,
  });
  const proposedAnalyticsColumns = columnsBuilder({
    columnDefs: proposedAnalyticsColumnDefs,
    fieldGetter: getFieldGetter('proposed'),
    columnSource: 'CONTRIBUTION',
    contributorsLimit,
    totalPortfolioAnalysis: getTotalPortfolioAnalysis ? getTotalPortfolioAnalysis('proposed') : undefined,
  });
  const differenceAnalyticsColumn = columnsBuilder({
    columnDefs: differenceAnalyticsColumnDefs,
    fieldGetter: getFieldGetter('difference'),
    columnSource: 'CONTRIBUTION',
    contributorsLimit,
    totalPortfolioAnalysis: undefined,
  });
  const posInfoColumns = columnsBuilder({
    columnDefs: posInfoColumnDefs,
    fieldGetter: (columnName) => `posInfo.${columnName}`,
    columnSource: 'POSINFO',
    contributorsLimit: undefined,
    totalPortfolioAnalysis: undefined,
    getCloakValue,
    entityDefs,
  });
  const securityColumns = columnsBuilder({
    columnDefs: securityColumnDefs,
    fieldGetter: (columnName) => `security.${columnName}`,
    columnSource: 'SECURITY',
  });
  const taxAnalyticsColumns = columnsBuilder({
    columnDefs: taxAnalyticsColumnDefs,
    fieldGetter: (columnName) => `taxAnalytics.${columnName}`,
    columnSource: 'TAX_ANALYTICS',
  });
  return {
    currentAnalyticsColumns,
    proposedAnalyticsColumns,
    differenceAnalyticsColumn,
    posInfoColumns,
    securityColumns,
    taxAnalyticsColumns,
  };
}

type ColumnDefSortOrder = 'asc' | 'desc' | undefined | null;
/***************************** CUSTOM AGGREGATION DATA FILTERING UTILS *****************************/
export type CustomAggFuncFlagsMapType = {
  [colId: string]: {
    treatNullAsZero: boolean;
    excludeZero: boolean;
  };
};

/**
 * Util function to map the column ID to its configured boolean flags for each configurable column.
 * This map will be stored in the context within gridOptions, for use during custom aggregation calculations.
 * The default value for these flags is false.
 * @param columnDefs
 */
export function getCustomAggFuncFlagsMap(columnDefs?: ConfigurableHoldingColumn[]): CustomAggFuncFlagsMapType {
  const customAggFuncFlags: CustomAggFuncFlagsMapType = {};
  columnDefs?.forEach((columnDef) => {
    customAggFuncFlags[columnDef.name] = {
      treatNullAsZero: columnDef.treatNullAsZero ?? false,
      excludeZero: columnDef.excludeZero ?? false,
    };
  });
  return customAggFuncFlags;
}

function getCustomAggFuncFlags({ context, colDef }: IAggFuncParams) {
  const gridContext = context as { currency: string; customAggFuncFlagsMap: CustomAggFuncFlagsMapType };

  // The information from the config that we need for custom agg funcs is stored in the context set via gridOptions
  const customAggFuncsFlagsMap = gridContext.customAggFuncFlagsMap;
  return colDef.colId ? customAggFuncsFlagsMap[colDef.colId] : { treatNullAsZero: false, excludeZero: false };
}

type ValuesToAggregateType = string | number | null;

function isNullOrUnknownValue(value: ValuesToAggregateType): boolean {
  if (value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return isNaN(parseFloat(value));
  }
  return false;
}

function treatNullAsZero(values: ValuesToAggregateType[]) {
  return values.map((value) => (isNullOrUnknownValue(value) ? 0 : value));
}

function excludeZero(values: ValuesToAggregateType[]) {
  return values.filter((value) => value !== 0);
}

interface FilterDataParamsType {
  excludeZero: boolean;
  treatNullAsZero: boolean;
  values: ValuesToAggregateType[];
}

/**
 * Util function to pre-filter the data according to configured boolean flags before calculating custom aggregation.
 * This function handles some edge cases according to which flags are set.
 * Null values will always be excluded by default, so that nulls do not cause the calculation to fail
 * If all values are null/being excluded, this function will return null.
 * @param params
 */
function filterValues(params: FilterDataParamsType) {
  const treatNullAsZeroFlag = params.treatNullAsZero;
  const excludeZeroFlag = params.excludeZero;
  const values = params.values;

  let filteredValues: ValuesToAggregateType[];

  if (treatNullAsZeroFlag) {
    filteredValues = treatNullAsZero(values);
  } else {
    filteredValues = removeNullValues(values);
  }

  if (excludeZeroFlag) {
    filteredValues = excludeZero(filteredValues);
  }

  // If all values are null/being excluded, return null to display null at the group level.
  return filteredValues.length === 0 ? null : filteredValues;
}

/**
 * Util function to convert filtered values to numbers for numeric aggregation calculations.
 * Handles both string and number inputs, converting strings to numbers via parseFloat.
 * Non-numeric strings are converted to 0.
 * @param values - Array of values that may be strings or numbers
 * @returns Array of numeric values
 */
function convertValues(values: ValuesToAggregateType[]): number[] {
  return values.map((val) => {
    if (typeof val === 'number') {
      return val;
    }
    if (typeof val === 'string') {
      const parsed = parseFloat(val);
      return !isNaN(parsed) ? parsed : 0;
    }
    return 0;
  });
}

/**
 * Util function for custom aggregation methods for when treatNullAsZero is false.
 * This will remove null values from the calculation. If the returned list is empty, we will display a dash at the group level.
 * @param values
 */
function removeNullValues(values: ValuesToAggregateType[]) {
  return values.filter((value) => !isNullOrUnknownValue(value));
}

/************************** CUSTOM AGGREGATION CALCULATION UTILS **************************/
function sumValues(values: ValuesToAggregateType[]) {
  return values.reduce((previousValue: number, currentValue) => {
    if (typeof currentValue === 'number') {
      return previousValue + currentValue;
    }
    return previousValue;
  }, 0);
}

function firstValues(values: ValuesToAggregateType[]) {
  return values.every((value) => value === values[0]) ? values[0] : null;
}

function weightedAverage(params: IAggFuncParams, excludeZero: boolean, treatNullAsZero: boolean) {
  let totalMarketValue = 0;
  const weightedMarketValue = params.rowNode.allLeafChildren?.reduce((previousValue, rowNode) => {
    let weightBy = get(rowNode.data, params.colDef.field ?? '', null);
    if (weightBy === null && treatNullAsZero) {
      weightBy = 0;
    }

    if ((weightBy === 0 && excludeZero) || weightBy === null) {
      // Skipping values if they are null or exclude zero is true and value is 0
      return previousValue;
    }
    const currentValue = rowNode.data.marketValue * weightBy;
    totalMarketValue += rowNode.data.marketValue;
    return currentValue + previousValue;
  }, 0);

  return totalMarketValue === 0 || isNaN(weightedMarketValue) || isNil(weightedMarketValue)
    ? null
    : weightedMarketValue / totalMarketValue;
}

/******************************************* CUSTOM AGGREGATION METHODS  *********************************************/
function customSum(params: IAggFuncParams) {
  const shouldAggregate = params.context?.shouldAggregateAnalytics;
  if (shouldAggregate && !shouldAggregate(params)) {
    return null;
  }

  const { treatNullAsZero, excludeZero } = getCustomAggFuncFlags(params);

  const filteredValues = filterValues({
    values: params.values,
    treatNullAsZero,
    excludeZero,
  });

  if (!filteredValues) {
    return null;
  }

  const convertedValues = convertValues(filteredValues);
  return sumValues(convertedValues);
}

function customAvg(params: IAggFuncParams) {
  const shouldAggregate = params.context?.shouldAggregateAnalytics;
  if (shouldAggregate && !shouldAggregate(params)) {
    return null;
  }
  const { treatNullAsZero, excludeZero } = getCustomAggFuncFlags(params);

  const filteredValues = filterValues({
    values: params.values,
    treatNullAsZero,
    excludeZero,
  });

  if (!filteredValues) {
    return null;
  }

  const convertedValues = convertValues(filteredValues);
  return mean(convertedValues);
}

function customFirst(params: IAggFuncParams) {
  const { treatNullAsZero, excludeZero } = getCustomAggFuncFlags(params);

  const filteredValues = filterValues({
    values: params.values,
    treatNullAsZero,
    excludeZero,
  });

  // Don't skip nulls for first method
  return !filteredValues || (!treatNullAsZero && params.values.some((value) => value === null))
    ? null
    : firstValues(filteredValues);
}

function customWeightedAvg(params: IAggFuncParams) {
  const shouldAggregate = params.context?.shouldAggregateAnalytics;
  if (shouldAggregate && !shouldAggregate(params)) {
    return null;
  }
  const { treatNullAsZero, excludeZero } = getCustomAggFuncFlags(params);

  const filteredValues = filterValues({
    values: params.values,
    treatNullAsZero,
    excludeZero,
  });

  // Weighted avg is a special case because the util func needs the full params, so filtering is just for validation checks
  return filteredValues ? weightedAverage(params, excludeZero, treatNullAsZero) : null;
}

/**
 * Custom aggregation function that excludes agggregate rows with assetType 'FX'.
 *
 * @param params - The aggregation function parameters.
 * @returns The sum of the numeric values in the `params.values` array, or null if all values are FX.
 */
function customSumExcludingFX<T extends { security: Security }>(params: IAggFuncParams<T>) {
  const fxFwdIdxs: number[] = [];
  if (params.rowNode.allLeafChildren?.length === params.values.length) {
    params.rowNode.allLeafChildren.forEach((child, idx) => {
      if (child.data?.security?.assetType === 'FX') {
        fxFwdIdxs.push(idx);
      }
    });
  }
  const nonFxValues = params.values.filter((_, i) => !fxFwdIdxs.includes(i));
  return isEmpty(nonFxValues)
    ? null
    : nonFxValues.reduce((sum: number, value) => {
        if (typeof value === 'number') {
          return sum + value;
        }
        return sum;
      }, 0);
}
/**
 * Liabilities treatment:
 * Custom aggregation function for liabilities treatment when leverage is applied that adjusts sums based on group context.
 * - For the 'Liabilities' group:
 *   - On 'weight' or 'proposedWeight' columns, render a dash.
 *   - On MV/proposed MV columns, sum only negative numeric values; if none exist, return 0.
 * - For the 'Gross Assets' group:
 *   - Sum values and add an optional positive liability offset from colDef.context.positiveLiability.
 * - For other groups:
 *   - Return the plain sum of numeric values.
 * @param params - The aggregation function parameters.
 * @returns The sum of the numeric values in the `params.values` array, adjusted per liability rules.
 */

function groupSumWithLiabilityAdjustments(params: IAggFuncParams) {
  const colCtx = params.colDef.context as { positiveLiability?: number; leverage?: LeverageConfig } | undefined;
  // Default to normal sum if no leverage config is present
  if (!colCtx?.leverage) {
    return sum(params.values);
  }
  const positiveLiability = typeof colCtx?.positiveLiability === 'number' ? colCtx.positiveLiability : 0;
  const isLiabilityGroup = params.rowNode?.key === colCtx?.leverage?.liabilitiesTableLabel;
  const isGrossAssetsGroup = params.rowNode?.key === colCtx?.leverage?.assetsTableLabel;
  if (isLiabilityGroup && (params.colDef.colId === 'weight' || params.colDef.colId === 'proposedWeight')) {
    return ONE_DASH;
  }
  if (isLiabilityGroup) {
    const negativeLiabilities = params.values.filter((value) => typeof value === 'number' && value < 0);
    if (negativeLiabilities.length > 0) {
      return sum(negativeLiabilities);
    } else {
      return 0;
    }
  }
  return sum(params.values) + (isGrossAssetsGroup ? positiveLiability : 0);
}

/**
 * 1770773 - these functions are custom aggregate functions for grouped columns at the top level.
 * This is used to register the functions with ag-grid. The functions do not apply to columns with source TAX_ANALYTICS or CONTRIBUTION
 */
export const customAggFuncs: {
  [key: string]: (params: IAggFuncParams) => string | number | null;
} = {
  customSum,
  customAvg,
  customWeightedAvg,
  customFirst,
  customSumExcludingFX,
  groupSumWithLiabilityAdjustments,
};

export function getHoldingsSortOrder(
  colDefId: string | undefined,
  holdingsInitialSort: HoldingsInitialSort | undefined,
  fallbackSort: ColumnDefSortOrder
): ColumnDefSortOrder {
  if (colDefId && holdingsInitialSort) {
    return colDefId === holdingsInitialSort.name ? holdingsInitialSort.sortOrder : undefined;
  }
  return fallbackSort;
}

export const redrawPinnedBottomRow = (gridApi: GridApi) => {
  const pinnedRow = gridApi.getPinnedBottomRow(0);
  if (pinnedRow) {
    gridApi.redrawRows({ rowNodes: [pinnedRow] });
  }
};

interface AddConfigurableGroupBysToGroupableColumnsParams {
  entityDefs?: Entity[];
  getCloakValue?: (aggIDTag: string, aggIDValue: string) => string;
  groupableColumns: ColDef[];
  groupByColumnDefs: ConfigurableHoldingColumn[];
  initialSort: HoldingsInitialSort | undefined;
  t: TFunction;
  toExport: HeaderLocation;
}

export const addConfigurableGroupBysToGroupableColumns = ({
  groupableColumns,
  groupByColumnDefs,
  initialSort,
  t,
  toExport,
  getCloakValue,
  entityDefs,
}: AddConfigurableGroupBysToGroupableColumnsParams) => {
  // If you have specified custom group by fields, we need to add these as hidden columns so they can be grouped by
  if (groupByColumnDefs.length) {
    groupableColumns.push(
      ...createCustomGroupByColumns({
        t,
        existingColumns: groupableColumns,
        userConfiguredGroupByColumns: groupByColumnDefs,
      }).map((col) => ({
        ...col,
        sort: getHoldingsSortOrder(col.colId, initialSort, col.sort),
        valueGetter: (params: ValueGetterParams) => {
          if (params.data) {
            if (params.data.isLiability) {
              return null;
            }
            // Use original valueGetter to preserve fallbackFields logic from createCustomGroupByColumns
            /* istanbul ignore else */
            return typeof col.valueGetter === 'function'
              ? col.valueGetter(params)
              : get(params.data, params.colDef?.field ?? '');
          } else if (params.node?.group) {
            const firstEle = params.node.allLeafChildren?.[0];
            return firstEle?.data?.isLiability ? null : params.node.key;
          }
          return typeof col.valueGetter === 'function' ? col.valueGetter(params) : undefined;
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (getCloakValue && entityDefs) {
            const isEntityField = getEntityGivenAggIDTag(entityDefs, params.colDef?.colId ?? '');
            if (isEntityField && params.colDef?.field?.startsWith('posInfo')) {
              return gridCloakValueFormatter(params.value, params.colDef?.colId ?? '', getCloakValue, entityDefs, t);
            }
          }
          return typeof col.valueFormatter === 'function' ? col.valueFormatter(params) : params.value;
        },
        headerValueGetter:
          col.headerName === 'Security Currency'
            ? (params: HeaderValueGetterParams) =>
                params?.location === toExport ? t('c_currency') : (col.headerName ?? '')
            : col.headerValueGetter,
      }))
    );
  }
};

export const isColGroupDef = (def: ColDef | ColGroupDef): def is ColGroupDef =>
  !def.hasOwnProperty('colId') && def.hasOwnProperty('children');

const findColDef = (defs: (ColDef | ColGroupDef)[], colId: string): ColDef | undefined => {
  for (const def of defs) {
    let found: ColDef | undefined;
    if (isColGroupDef(def)) {
      found = findColDef(def.children, colId);
    } else {
      found = def.colId === colId ? def : undefined;
    }
    if (found) {
      return found;
    }
  }
  return undefined;
};

export const enhanceColumnsWithDynamicSleeveAwareness = (
  groupings: string[],
  columnDefs: (ColDef | ColGroupDef)[],
  groupByColumnDefs: ConfigurableHoldingColumn[],
  sleeveGetter: (tag: string, value: string) => SleeveInfo | undefined,
  noSleeveText?: string
) => {
  // This builds sleeve aware overrides to columns that are 1) posInfo based, 2) contain sleeve overrides, 3) are actively being grouped
  const sleeveAwareColumnGroupings = groupByColumnDefs.filter(
    (col) => col.field?.startsWith('posInfo.') && col.sleeveInfoOverrides && groupings.includes(col.name)
  );
  const lastGrouping = groupings[groupings.length - 1];
  sleeveAwareColumnGroupings.forEach((sleeveGrouping) => {
    const sleeveTag = sleeveGrouping.field?.substring('posInfo.'.length);
    const currentColDef = findColDef(columnDefs, sleeveGrouping.name);
    if (currentColDef) {
      const currentColField = currentColDef.field;
      const currentValueGetter =
        typeof currentColDef.valueGetter === 'function' ? currentColDef.valueGetter : undefined;
      currentColDef.valueGetter = (params) => {
        const { data, node } = params;
        if (currentValueGetter) {
          return currentValueGetter(params) ?? (sleeveGrouping.name === lastGrouping ? undefined : noSleeveText); // we don't want to show sleeve groupings if it could be a leaf level node
        } else if (node?.group) {
          return node.groupData?.['ag-Grid-AutoColumn'] ?? noSleeveText;
        } else if (currentColField) {
          const sleeveValue = get(data, currentColField);
          if (!isNil(sleeveValue)) {
            return (data.portfolioName ?? '') + '|' + sleeveValue;
          }
        }
        return noSleeveText;
      };

      const currentValueFormatter =
        typeof currentColDef.valueFormatter === 'function' ? currentColDef.valueFormatter : undefined;
      currentColDef.valueFormatter = (params) => {
        const { value, data, node } = params;
        let valueFormatted = value;
        if (node) {
          if (node.group) {
            const portfolioName = node?.allLeafChildren?.[0]?.data?.portfolioName ?? '';
            const stringToSearch = portfolioName + '|';
            if (value?.startsWith(stringToSearch)) {
              valueFormatted = value.substring(stringToSearch.length);
            }
          } else {
            if (value && typeof value === 'string' && data) {
              const stringToSearch = (data.portfolioName ?? '') + '|';
              if (value.startsWith(stringToSearch)) {
                valueFormatted = value.substring(stringToSearch.length);
              }
            }
          }
        }
        return currentValueFormatter ? currentValueFormatter({ ...params, value: valueFormatted }) : valueFormatted;
      };
      sleeveGrouping.sleeveInfoOverrides?.forEach(({ sleeveInfoField, colDefName }) => {
        const existingColumn = findColDef(columnDefs, colDefName); // we know it is a col def at this point
        if (existingColumn && sleeveTag) {
          if (existingColumn.rowGroupIndex && existingColumn.rowGroupIndex > (currentColDef.rowGroupIndex ?? 0)) {
            return;
          }
          const existingValueGetter = existingColumn.valueGetter;
          const existingField = existingColumn.field;
          existingColumn.aggFunc = (params) => {
            let firstValue;
            if (params.values.length) {
              firstValue = params.values[0];
              for (let i = 1; i < params.values.length; i += 1) {
                if (params.values[i] !== firstValue) {
                  return '';
                }
              }
              return firstValue;
            }
          };
          existingColumn.valueGetter = (params) => {
            const { node, data } = params;
            if (node) {
              if (node.group) {
                if (node.key && node.field === sleeveGrouping.field) {
                  return node.key;
                }
              } else {
                if (data) {
                  const sleeveValue = get(data.posInfo, sleeveTag);
                  const sleeveInfo = sleeveGetter(sleeveTag, sleeveValue);
                  if (sleeveInfo) {
                    const portfolioNamePrefix =
                      existingColumn === currentColDef ? (data.portfolioName ?? '') + '|' : '';
                    return portfolioNamePrefix + get(sleeveInfo?.metadata, sleeveInfoField, '');
                  }
                }
              }
            }
            const existingFieldValue = existingField ? get(data ?? node?.aggData, existingField) : undefined;
            return typeof existingValueGetter === 'function' ? existingValueGetter(params) : existingFieldValue;
          };
        }
      });
    }
  });
};

/**
 * Identifies if a given column definition represents a divider column.
 * Additionally, the presence of a specific `cellClass` starting with 'dividerColDef' is used as a heuristic for identification.
 *
 * @param column - The column definition to check.
 * @returns `true` if the column is a divider column, otherwise `false`.
 */
export const isDividerColumn = (column: Column) => {
  const cellClass = column?.getUserProvidedColDef()?.cellClass;
  if (cellClass && typeof cellClass === 'string') {
    return cellClass.startsWith('dividerColDef');
  }
  return false;
};

export const getSleeveIdOverride = (
  params: ValueFormatterParams,
  sleeveInfoStore: SleeveInfoStoreState,
  sleeveIdentifierField: string,
  getSleeveInfo: (
    state: SleeveInfoStoreState,
    tag: string,
    value: string,
    portfolio: ProposablePortfolio_TEMP | undefined
  ) => SleeveInfo | undefined,
  portfolio?: ProposablePortfolio_TEMP
): string => {
  const tag = params?.node?.field?.split('.')?.[1] ?? '';
  const value = params.value;
  const sleeveInfo = getSleeveInfo(sleeveInfoStore, tag, value, portfolio);
  return get(sleeveInfo?.metadata, sleeveIdentifierField, value);
};

export const orderRowNodesByConfiguredFieldOrder = (
  nodes: IRowNode[],
  groupByColumns: ConfigurableHoldingColumn[]
): IRowNode[] => {
  const groupByFieldOrderConfigIsPresent = groupByColumns.find((colDef) => !isEmpty(colDef.fieldGroupByOrder));
  const nodeKey = nodes[0]?.key;
  if (!nodeKey || !groupByFieldOrderConfigIsPresent) {
    return nodes;
  }
  let groupByColumnsCopy = [...groupByColumns];
  const nodeKeyList: string[] = nodes.reduce((nodesAccumulator: string[], node) => {
    if (node.key) {
      nodesAccumulator[nodesAccumulator.length] = node.key;
    }
    return nodesAccumulator;
  }, []);
  const nodesToBeSorted = [...nodes];
  const sampleNodeField = nodes[0]?.field;
  const groupByColumnItemMatch = groupByColumnsCopy.filter(
    (groupByColumnItem) => groupByColumnItem.field === sampleNodeField
  );
  if (sampleNodeField && !isEmpty(groupByColumnItemMatch)) {
    groupByColumnsCopy = groupByColumnItemMatch;
  }
  // Get a list of groupByColumns.colDefs that include a configured display order
  const groupColDefOrderedByCustomConfig = groupByColumnsCopy.find((colDef) =>
    colDef.fieldGroupByOrder?.some((fieldGroupByOrderItem) => nodeKeyList.includes(fieldGroupByOrderItem))
  );
  if (groupColDefOrderedByCustomConfig) {
    // If we have some then order the targeted rows per the config to eventually be rendered in that order
    const sortOrder = groupColDefOrderedByCustomConfig.fieldGroupByOrder || [];
    nodesToBeSorted.sort((a, b) => sortOrder.indexOf(a.key || '') - sortOrder.indexOf(b.key || ''));
  }
  return nodesToBeSorted;
};

export const autoSizeColumns = (gridApi: GridApi, columnWidths: { key: string; newWidth: number }[] | undefined) => {
  const allColumnIds: string[] = [];
  if (isEmpty(columnWidths) && !columnWidths) {
    gridApi.autoSizeAllColumns(true);
    return;
  }
  const columns = columnWidths?.map((col) => col.key);
  gridApi.getAllDisplayedColumns()?.forEach((column) => {
    const colId = column.getId();
    if (!columns?.includes(colId)) {
      allColumnIds.push(colId);
    }
  });
  gridApi.autoSizeColumns(allColumnIds, true);
};

/**
 * ExtractCellParams<TCellComponent> strongly enforce that the parameters you pass into cellRenderer or cellEditor params actually matches a given CellRenderer or CellEditor's props
 *
 * @example
 *
 *   // Given a cell renderer
 *   const CellRenderer = (props: ICellRendererParams<{}> & { hello: string }) => <div>{hello}</div>;
 *
 *   // The following code should throw TS1360 - property 'hello' is missing
 *   const columnDefinition = {
 *      cellRenderer: CellRenderer,
 *      cellRendererParams: {} satisfies ExtractCellParams<typeof CellRenderer>;
 *   }
 *
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- existing any
export type ExtractCellParams<TCellComponent> = TCellComponent extends (props: infer TProps, ...theRest: any) => any
  ? TProps extends ICellRendererParams<infer TData>
    ? Omit<TProps, keyof ICellRendererParams<TData>> // removes default ICellRendererParams to avoid pollution
    : TProps extends ICellEditorParams<infer TData>
      ? Omit<TProps, keyof ICellEditorParams<TData>> // removes default ICellEditorParams to avoid pollution
      : TProps
  : unknown;

/**
 * ExtractHeaderComponentParams<THeaderComponent> strongly enforce that the parameters you pass into headerComponent params actually matches a given HeaderComponent props
 *
 * @example
 *
 *   // Given a header component
 *   const CellRenderer = (props: ICellRendererParams<{}> & { hello: string }) => <div>{hello}</div>;
 *
 *   // The following code should throw TS1360 - property 'hello' is missing
 *   const columnDefinition = {
 *      cellRenderer: CellRenderer,
 *      cellRendererParams: {} satisfies ExtractCellParams<typeof CellRenderer>;
 *   }
 *
 */
export type ExtractHeaderComponentParams<THeaderComponent> = THeaderComponent extends (
  props: infer TProps,
  ...theRest: any // eslint-disable-line @typescript-eslint/no-explicit-any -- existing any
) => any // eslint-disable-line @typescript-eslint/no-explicit-any -- existing any
  ? TProps extends IHeaderParams<infer TData>
    ? Omit<TProps, keyof IHeaderParams<TData>> // removes default ICellRendererParams to avoid pollution
    : TProps
  : unknown;

/**
 * When copying cells to clipboard, if the valueFormatter returns an empty string, do not copy any value for this cell.
 * This is to prevent hidden data being copied to the clipboard despite no value displayed for that cell.
 * @param params
 */
export const processCellForClipboard = (params: ProcessCellForExportParams) => {
  const colDef: ColDef = params.column.getColDef();

  if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function' && params.node) {
    const formattedValue = colDef.valueFormatter({
      node: params.node,
      data: params.node.data,
      colDef,
      value: params.value,
      column: params.column,
      api: params.api,
      context: params.context,
    });

    if (formattedValue === '') {
      return '';
    }
  }

  return params.value;
};
